/**
 * Project list. What each person sees is decided by the Worker (PLAN.md 10,
 * Phase 2): admins all projects, managers the ones they created, executives
 * the work projects they were added to. Filters here only narrow that list.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { api } from '../api/client';
import type { Label, ProjectKind, ProjectSummary } from '../api/types';
import { useCan, useUser } from '../auth/session';
import { Badge, Button, ErrorBox, Field, formatDateTime, Modal, Spinner, TableCard, TextInput } from '../components/ui';

export const KIND_LABEL: Record<ProjectKind, string> = { work: 'Work', share: 'Share' };
export const KIND_HINT: Record<ProjectKind, string> = {
  work: 'Executives you add can fill documents from its templates.',
  share: 'Holds templates only, to share by a link that opens without signing in. No members, no documents.',
};

function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ProjectKind>('work');
  const create = useMutation({
    mutationFn: () => api<{ project: ProjectSummary }>('POST', '/api/projects', { body: { name: name.trim(), kind } }),
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      setName('');
      onClose();
      void navigate({ to: '/projects/$id', params: { id: r.project.id } });
    },
  });
  return (
    <Modal open={open} title="New project" onClose={onClose}>
      <form className="flex flex-col gap-3" onSubmit={e => { e.preventDefault(); if (name.trim()) create.mutate(); }}>
        <Field label="Name">{id => <TextInput id={id} value={name} onChange={e => setName(e.target.value)} maxLength={120} required autoFocus placeholder="e.g. Guntur registrations, October" />}</Field>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Kind (cannot be changed later)</legend>
          {(['work', 'share'] as const).map(k => (
            <label key={k} className="flex items-start gap-2 text-sm">
              <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
              <span><strong>{KIND_LABEL[k]}</strong>: {KIND_HINT[k]}</span>
            </label>
          ))}
        </fieldset>
        <ErrorBox error={create.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="primary" type="submit" disabled={!name.trim() || create.isPending}>Create</Button>
        </div>
      </form>
    </Modal>
  );
}

/** The list with its filters; used by the Projects page and the Home dashboard. */
export function ProjectList() {
  const { actor } = useUser();
  const canCreate = useCan('project.create');
  const [creating, setCreating] = useState(false);
  const [labelFilter, setLabelFilter] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | ProjectKind>('');
  const [search, setSearch] = useState('');

  const list = useQuery({ queryKey: ['projects'], queryFn: () => api<{ projects: ProjectSummary[] }>('GET', '/api/projects') });
  const labels = useQuery({ queryKey: ['labels'], queryFn: () => api<{ labels: Label[] }>('GET', '/api/labels') });
  const isExec = actor === 'executive_view' || actor === 'executive_editor';

  const rows = (list.data?.projects ?? []).filter(p =>
    (!labelFilter || p.labels.some(l => l.id === labelFilter)) &&
    (!kindFilter || p.kind === kindFilter) &&
    (!search.trim() || p.name.toLowerCase().includes(search.trim().toLowerCase())));

  return (
    <section aria-labelledby="projects-heading" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="projects-heading" className="text-lg font-semibold">{isExec ? 'Your projects' : 'Projects'}</h2>
        {canCreate && <Button tone="primary" onClick={() => setCreating(true)}>New project</Button>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">Search
          <TextInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Project name" />
        </label>
        <label className="flex flex-col text-sm">Label
          <select className="rounded border border-slate-300 px-2 py-1.5 text-sm" value={labelFilter} onChange={e => setLabelFilter(e.target.value)}>
            <option value="">All labels</option>
            {labels.data?.labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        {!isExec && (
          <label className="flex flex-col text-sm">Kind
            <select className="rounded border border-slate-300 px-2 py-1.5 text-sm" value={kindFilter} onChange={e => setKindFilter(e.target.value as '' | ProjectKind)}>
              <option value="">Work and share</option>
              <option value="work">Work</option>
              <option value="share">Share</option>
            </select>
          </label>
        )}
      </div>

      {list.isPending ? <Spinner /> : list.isError ? <ErrorBox error={list.error} /> : (
        <TableCard>
          <caption className="sr-only">Projects</caption>
          <thead>
            <tr className="border-b border-slate-300 text-left">
              <th scope="col" className="py-2">Name</th>
              {!isExec && <th scope="col">Kind</th>}
              <th scope="col">Templates</th>
              {!isExec && <th scope="col">Members</th>}
              {actor === 'admin' && <th scope="col">Created by</th>}
              <th scope="col">Labels</th>
              <th scope="col">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.id} className="border-b border-slate-200">
                <td className="py-2"><Link to="/projects/$id" params={{ id: p.id }} className="font-medium text-blue-800 underline">{p.name}</Link></td>
                {!isExec && <td><Badge tone={p.kind === 'work' ? 'blue' : 'amber'}>{KIND_LABEL[p.kind]}</Badge></td>}
                <td>{p.templateCount}</td>
                {!isExec && <td>{p.kind === 'work' ? p.memberCount : '—'}</td>}
                {actor === 'admin' && <td>{p.createdBy.displayName} <code className="text-xs text-slate-500">{p.createdBy.userCode}</code></td>}
                <td>{p.labels.map(l => <Badge key={l.id}>{l.name}</Badge>)}</td>
                <td className="text-xs text-slate-600">{formatDateTime(p.updatedAt)}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="py-6 text-center text-slate-500">
                {labelFilter || kindFilter || search ? 'No projects match.' : isExec ? 'No projects yet. Your manager adds you to projects.' : 'No projects yet.'}
              </td></tr>
            )}
          </tbody>
        </TableCard>
      )}
      {canCreate && <NewProjectDialog open={creating} onClose={() => setCreating(false)} />}
    </section>
  );
}

export function ProjectsPage() {
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 p-6">
      <h1 className="sr-only">Projects</h1>
      <ProjectList />
    </div>
  );
}
